import {
  ArrowUpDown,
  Calendar,
  ChevronDown,
  Eye,
  MoreHorizontal
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { getCampaignExpirationText } from '@/ducks/campaigns/campaigns.helpers'
import type { Campaign, CampaignLimit } from '@/ducks/campaigns/campaigns.types'

import CampaignOfferCell from './CampaignOfferCell'
import CampaignStatusCell from './CampaignStatusCell'
import CampaignsTableFooter from './CampaignsTableFooter'
import Styles from './CampaignsTable.module.scss'

type Props = {
  campaigns: Campaign[]
  page: number
  limit: CampaignLimit
  total: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: CampaignLimit) => void
}

const CampaignsTable = ({
  campaigns,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange
}: Props) => {
  const { t } = useTranslation()

  return (
    <div className={Styles.wrapper} data-testid="campaigns-table">
      <div className={Styles.filtersRow}>
        <button type="button" className={Styles.chip} disabled>
          <span>{t('marketing.campaigns.filters.types')}</span>
          <ChevronDown width={20} height={20} />
        </button>
        <button type="button" className={Styles.chip} disabled>
          <span>{t('marketing.campaigns.filters.status')}</span>
          <ChevronDown width={20} height={20} />
        </button>
        <button type="button" className={Styles.chip} disabled>
          <Calendar width={20} height={20} />
          <span>{t('marketing.campaigns.filters.date')}</span>
          <ChevronDown width={20} height={20} />
        </button>
      </div>

      <div className={Styles.tableWrapper}>
        <table className={Styles.table}>
          <colgroup>
            <col className={Styles.offerCol} />
            <col className={Styles.statusCol} />
            <col className={Styles.expirationCol} />
            <col className={Styles.actionsCol} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <span>{t('marketing.campaigns.table.offer')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('marketing.campaigns.table.status')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('marketing.campaigns.table.expiration')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th className={Styles.actionsHeader}>
                <span>{t('marketing.campaigns.table.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map(campaign => (
                <tr key={campaign.campaignId}>
                  <td className={Styles.offerCell}>
                    <CampaignOfferCell campaign={campaign} />
                  </td>
                  <td className={Styles.statusCell}>
                    <CampaignStatusCell campaign={campaign} />
                  </td>
                  <td className={Styles.expirationCell}>
                    {getCampaignExpirationText(campaign, t)}
                  </td>
                  <td className={Styles.actionsCellColumn}>
                    <div className={Styles.actionsCell}>
                      <button
                        type="button"
                        className={Styles.iconButton}
                        disabled
                        aria-label={t('marketing.campaigns.view-details')}>
                        <Eye width={16} height={16} />
                      </button>
                      <button
                        type="button"
                        className={Styles.iconButton}
                        disabled
                        aria-label={t('marketing.campaigns.more-actions')}>
                        <MoreHorizontal width={16} height={16} />
                      </button>
                    </div>
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CampaignsTableFooter
        page={page}
        limit={limit}
        total={total}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />
    </div>
  )
}

export default CampaignsTable
